<template>
  <v-app :key="selected.aws_resource_name" id="inspire">
    <v-main class="auth-main">
      <v-row no-gutters class="auth-layout-row">
        <v-col
          class="image tw-hidden lg:tw-block image tw-h-auto tw-flex tw-justify-center"
          cols="12"
          sm="12"
          md="6"
          lg="6"
        >
          <div class="tw-w-full tw-h-screen tw-flex tw-justify-center tw-items-end">
            <div class="tw-w-full tw-flex flex-wrap tw-justify-start tw-mt-10 lg:tw-mt-0 tw-px-2 md:tw-px-4 tw-mb-20">
              <div class="tw-w-full tw-flex tw-items-center tw-justify-start tw-text-left">
                <div class="tw-px-2">
                  <h1 class="c-white-text tw-text-xl md:tw-text-2xl lg:tw-text-3xl tw-font-bold tw-leading-tight">
                    {{ heroTitle }}
                  </h1>
                  <h3 class="tw-text-base md:tw-text-lg c-white-text tw-opacity-80 tw-mt-2 tw-font-medium">
                    {{ heroSubtitle }}
                  </h3>
                </div>
              </div>
              <div
                v-if="showCustomerPromo"
                class="tw-px-16 tw-w-full tw-mt-4 tw-text-left tw-text-white tw-flex tw-items-center"
              >
                <a
                  id="playstore-link"
                  href="https://play.google.com/store/apps/details?id=ke.co.apollo.client.app"
                  target="_blank"
                  rel="noopener"
                >
                  <img src="/images/google-play-badge.png" width="130" alt="play store">
                </a>
                <p class="tw-text-xs md:tw-text-base md:tw-text-left tw-px-2 tw-mt-3">
                  You can also download our Motor Happiness App on the Google play store
                </p>
              </div>
            </div>
          </div>
        </v-col>

        <auth-form-panel panel-id="login">
          <div class="auth-pool-tabs">
            <button
              type="button"
              :class="['auth-pool-tab', !isAgentPortal ? 'auth-pool-tab--active' : '']"
              @click="selectPool(configurations[0])"
            >
              Customer
            </button>
            <button
              type="button"
              :class="['auth-pool-tab', isAgentPortal ? 'auth-pool-tab--active' : '']"
              @click="selectPool(configurations[1])"
            >
              Agent
            </button>
          </div>

          <v-form v-model="isValid" ref="form" @submit.prevent="onSignIn">
            <label class="auth-field-label" for="email">Email</label>
            <v-text-field
              id="email"
              v-model="form.email"
              class="auth-input tw-mb-5"
              type="email"
              placeholder="Enter your email"
              outlined
              dense
              hide-details="auto"
              :rules="[required('Email'), emailFormat()]"
            />

            <div class="auth-field-label-row">
              <label class="auth-field-label tw-mb-0" for="password">Password</label>
              <router-link class="auth-field-link" :to="{ name: 'ForgotPassword', query: authLinkQuery }">
                Forgot password?
              </router-link>
            </div>
            <v-text-field
              id="password"
              v-model="form.password"
              class="auth-input tw-mb-2"
              :type="passwordFieldType"
              placeholder="Enter password"
              outlined
              dense
              hide-details="auto"
              :rules="[required('Password')]"
              :append-icon="isPasswordVisible ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append="togglePasswordVisibility"
            />

            <p v-if="!isAgentPortal" class="auth-muted">
              Don't have an account?
              <router-link :to="{ name: 'SignUp', query: authLinkQuery }">Sign Up</router-link>
            </p>

            <v-btn
              id="signIn"
              type="submit"
              block
              class="auth-btn-pill auth-btn-pill--primary"
              :loading="loading"
              :disabled="!isValid"
            >
              Log In
            </v-btn>

            <need-help-dialog />

            <v-btn
              v-if="!isAgentPortal"
              block
              outlined
              class="auth-btn-pill auth-btn-pill--outline tw-mt-3"
              :to="{ name: 'GuestProductSelect' }"
            >
              Get Quote
            </v-btn>
          </v-form>
        </auth-form-panel>
      </v-row>
    </v-main>
  </v-app>
</template>

<script>
import AuthFormPanel from '@/components/auth/AuthFormPanel';
import NeedHelpDialog from '@/components/auth/NeedHelpDialog';
import validations from '@/utils/validations';
import { bootstrapPortalCache } from '@/utils/portalBootstrap';
import { mapGetters } from 'vuex';
import cookie from 'vue-cookies';
import { FB_SIGNIN, USER_TYPE } from '@/utils/const';
import Amplify from '@aws-amplify/core';
import AWSResources from '@/utils/aws-exports';
import { resolveMotorV2BuyRoute } from '@/utils/motorChannel';
import { getReferralAgentCode } from '@/services/referralApi';
import {
  isAgentPool,
  mergePortalQuery,
  persistPortalPool,
  resolveInitialPool,
} from '@/utils/portalPool';

export default {
  metaInfo: { title: 'Sign In' },
  components: { AuthFormPanel, NeedHelpDialog },
  data() {
    const selected = resolveInitialPool(AWSResources, null);
    return {
      form: { email: '', password: '' },
      ...validations,
      loading: false,
      isValid: false,
      isPasswordVisible: false,
      query: '',
      show: false,
      selected,
      configurations: AWSResources,
    };
  },
  created() {
    Amplify.configure(this.selected);
    persistPortalPool(this.selected);
  },
  computed: {
    ...mapGetters('auth', ['hasAuthenticationStatus', 'authenticationStatus', 'authenticatedUser']),
    passwordFieldType() {
      return this.isPasswordVisible ? 'text' : 'password';
    },
    isAgentPortal() {
      return isAgentPool(this.selected);
    },
    heroTitle() {
      return this.isAgentPortal
        ? 'Digital Underwriting Excellence.'
        : 'Your insurance, your way.';
    },
    heroSubtitle() {
      return this.isAgentPortal
        ? 'Quote, Issue & Manage Policies with Seamless Precision.'
        : 'Buy cover, manage policies, and report claims in minutes.';
    },
    showCustomerPromo() {
      return this.show && !this.isAgentPortal;
    },
    authLinkQuery() {
      return mergePortalQuery(this.$route.query, this.selected);
    },
  },
  mounted() {
    this.$store.dispatch('auth/clearAuthenticationStatus');
    const quote = this.$route.query.q;
    const benefit = this.$route.query.b;
    if (quote && benefit) this.query = this.$route.query;
    if (this.$route.query.portal) {
      this.selected = resolveInitialPool(AWSResources, this.$route);
      Amplify.configure(this.selected);
      persistPortalPool(this.selected);
    }
  },
  methods: {
    selectPool(config) {
      this.selected = config;
      Amplify.configure(this.selected);
      persistPortalPool(this.selected);
    },
    togglePasswordVisibility() {
      this.isPasswordVisible = !this.isPasswordVisible;
    },
    resolvePostLoginRoute() {
      if (this.$route.query.r) {
        const returnUrl = this.$route.query.r;
        const isCustomerDashboard = returnUrl.includes('/dashboard')
          && !returnUrl.includes('agent-dashboard')
          && !returnUrl.includes('/agent/');
        const isAgentPath = returnUrl.includes('agent-dashboard') || returnUrl.includes('/agent/');
        if (this.isAgentPortal && isCustomerDashboard) {
          return { type: 'name', name: 'AgentDashboard' };
        }
        if (!this.isAgentPortal && isAgentPath) {
          return { type: 'name', name: 'Dashboard' };
        }
        return { type: 'href', href: returnUrl };
      }
      if (this.query !== '' && !this.isAgentPortal) {
        const v2Route = resolveMotorV2BuyRoute(this.query);
        if (v2Route) {
          return { type: 'name', name: v2Route.name, query: v2Route.query };
        }
        return { type: 'name', name: 'BuyCover', query: this.query };
      }
      if (this.isAgentPortal) {
        return { type: 'name', name: 'AgentDashboard' };
      }
      // Shared agent link: land on products with agentCode so picker can lock.
      const agentCode = getReferralAgentCode(this.$route.query);
      if (!this.isAgentPortal && agentCode) {
        return {
          type: 'name',
          name: 'CustomerProductSelect',
          query: { agentCode },
        };
      }
      return { type: 'name', name: 'Dashboard' };
    },
    async onSignIn() {
      if (!this.$refs.form.validate()) return;
      this.loading = true;
      try {
        await this.$store.dispatch('auth/signIn', {
          username: this.form.email,
          password: this.form.password,
        });

        if (!this.hasAuthenticationStatus) return;
        if (this.authenticationStatus.variant === 'error') {
          this.$toast.error(this.authenticationStatus.message, 'Error');
          return;
        }

        this.$analytics.setUserId(this.authenticatedUser.email);
        this.$analytics.setUserProperties({
          account_type: this.isAgentPortal ? 'Agent-Portal' : 'Customer-Portal',
        });
        this.$analytics.logEvent(FB_SIGNIN, { email: this.authenticatedUser.email });

        if (this.isAgentPortal) cookie.set(USER_TYPE, 'agent');
        else cookie.remove(USER_TYPE);
        persistPortalPool(this.selected);

        // Navigate immediately — cache warm-up must not block the login spinner.
        const dest = this.resolvePostLoginRoute();
        if (dest.type === 'href') {
          bootstrapPortalCache(this.$store, {
            channel: this.isAgentPortal ? 'agent' : 'customer',
          }).catch(() => {});
          window.location.href = dest.href;
          return;
        }
        if (this.isAgentPortal) {
          this.$toast.success('Login successful', 'Success');
        }
        await this.$router.push({ name: dest.name, query: dest.query });
        bootstrapPortalCache(this.$store, {
          channel: this.isAgentPortal ? 'agent' : 'customer',
        }).catch(() => {});
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.image {
  background-image: url('/images/bg-1.jpg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  background-attachment: cover;
}
</style>
